import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle2, XCircle, Target, Bell, AlertTriangle, Info, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = {
  objetivo: { label: 'Objetivos', icon: Target, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  recordatorio: { label: 'Recordatorio', icon: Bell, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  importante: { label: 'Importante', icon: AlertTriangle, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  nota: { label: 'Nota', icon: Info, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
};

const resolveImg = (url) => (url && url.startsWith('/api') ? `${BACKEND}${url}` : url);
const norm = (s) => String(s || '').trim().toLowerCase();

const getEmbedUrl = (url) => {
  if (!url) return null;
  let u = String(url).trim();
  // Si pegan el código de inserción completo (<iframe ...>), extraer el src
  if (u.includes('<iframe')) {
    const m = u.match(/src=["']([^"']+)["']/i);
    if (m) u = m[1];
  }
  u = u.replace(/&amp;/g, '&');
  const yt = u.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  if (yt && yt[2]?.length === 11) return `https://www.youtube.com/embed/${yt[2]}`;
  if (u.includes('player.vimeo.com')) return u;
  const vm = u.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)(?:\/(\w+))?/);
  if (vm) {
    let hash = vm[2];
    if (!hash) { const h = u.match(/[?&]h=(\w+)/); if (h) hash = h[1]; }
    return `https://player.vimeo.com/video/${vm[1]}${hash ? `?h=${hash}` : ''}`;
  }
  return u;
};

const isLocalVideo = (url) => (url || '').startsWith('/api/uploads/video/') || isVideoFile(url);

const proseStyles = (
  <style>{`
    .lesson-prose h1 { font-size: 1.6rem; font-weight: 700; margin-bottom: .75rem; color: #1e293b; }
    .lesson-prose h2 { font-size: 1.35rem; font-weight: 700; margin-bottom: .6rem; color: #1e293b; }
    .lesson-prose h3 { font-size: 1.15rem; font-weight: 600; margin-bottom: .5rem; color: #1e293b; }
    .lesson-prose p { margin-bottom: .85rem; line-height: 1.75; color: #475569; }
    .lesson-prose ul { list-style: disc; padding-left: 1.5rem; margin-bottom: .85rem; }
    .lesson-prose ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: .85rem; }
    .lesson-prose li { margin-bottom: .35rem; color: #475569; }
    .lesson-prose a { color: #004E89; text-decoration: underline; }
    .lesson-prose strong { font-weight: 700; }
  `}</style>
);

const isVideoFile = (url) => /\.(mp4|webm|ogg)$/i.test(url || '');

const Shell = ({ children, submitted, isCorrect, explicacion, onCheck, onReset, canCheck }) => (
  <div className="border border-slate-200 rounded-xl p-5 bg-gradient-to-br from-slate-50 to-white" data-testid="question-block">
    {children}
    {!submitted ? (
      <Button type="button" onClick={onCheck} disabled={!canCheck} className="mt-4 bg-[#004E89] hover:bg-[#003B66]" data-testid="question-check-btn">Comprobar</Button>
    ) : (
      <div className="mt-4 space-y-3">
        <div className={`flex items-center gap-2 font-medium ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`} data-testid="question-result">
          {isCorrect ? <><CheckCircle2 className="w-5 h-5" /> ¡Correcto!</> : <><XCircle className="w-5 h-5" /> Incorrecto</>}
        </div>
        {explicacion && <div className="text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded-lg p-3">{explicacion}</div>}
        <Button type="button" variant="outline" size="sm" onClick={onReset} data-testid="question-retry-btn">Intentar de nuevo</Button>
      </div>
    )}
  </div>
);

const Prompt = ({ text }) => (
  <div className="flex items-start gap-2 mb-3">
    <HelpCircle className="w-5 h-5 text-[#004E89] mt-0.5 flex-shrink-0" />
    <p className="font-semibold text-slate-800">{text}</p>
  </div>
);

const OpcionQuestion = ({ block, onAnswered }) => {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const multiple = !!block.multiple;
  const toggle = (optId) => {
    if (submitted) return;
    if (multiple) setSelected((s) => (s.includes(optId) ? s.filter((x) => x !== optId) : [...s, optId]));
    else setSelected([optId]);
  };
  const correctIds = (block.opciones || []).filter((o) => o.correcta).map((o) => o.id);
  const isCorrect = submitted && selected.length === correctIds.length && selected.every((id) => correctIds.includes(id));
  const check = () => { setSubmitted(true); onAnswered(); };
  return (
    <Shell submitted={submitted} isCorrect={isCorrect} explicacion={block.explicacion} onCheck={check} onReset={() => { setSelected([]); setSubmitted(false); }} canCheck={selected.length > 0}>
      <Prompt text={block.pregunta} />
      <div className="space-y-2">
        {(block.opciones || []).map((o) => {
          const chosen = selected.includes(o.id);
          let cls = 'border-slate-200 hover:border-[#004E89]/40 bg-white';
          if (submitted) {
            if (o.correcta) cls = 'border-emerald-400 bg-emerald-50';
            else if (chosen && !o.correcta) cls = 'border-red-400 bg-red-50';
            else cls = 'border-slate-200 bg-white opacity-70';
          } else if (chosen) cls = 'border-[#004E89] bg-[#004E89]/5';
          return (
            <button key={o.id} type="button" onClick={() => toggle(o.id)} disabled={submitted}
              className={`w-full flex items-center gap-3 text-left px-4 py-2.5 rounded-lg border transition-colors ${cls}`} data-testid="question-option">
              <span className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center ${multiple ? 'rounded-md' : 'rounded-full'} ${chosen ? 'border-[#004E89] bg-[#004E89] text-white' : 'border-slate-300'}`}>
                {chosen && <span className="w-2 h-2 bg-white rounded-sm" />}
              </span>
              <span className="text-sm text-slate-700 flex-1">{o.texto}</span>
              {submitted && o.correcta && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
              {submitted && chosen && !o.correcta && <XCircle className="w-5 h-5 text-red-500" />}
            </button>
          );
        })}
      </div>
    </Shell>
  );
};

const HuecosQuestion = ({ block, onAnswered }) => {
  const segments = useMemo(() => String(block.enunciado || '').split(/_{3,}/), [block.enunciado]);
  const huecos = block.huecos || [];
  const [values, setValues] = useState(() => huecos.map(() => ''));
  const [submitted, setSubmitted] = useState(false);
  const results = huecos.map((h, i) => (h.respuestas || []).some((r) => norm(r) === norm(values[i])));
  const isCorrect = submitted && results.every(Boolean);
  const check = () => { setSubmitted(true); onAnswered(); };
  const reset = () => { setValues(huecos.map(() => '')); setSubmitted(false); };
  const canCheck = values.every((v) => v.trim() !== '');
  return (
    <Shell submitted={submitted} isCorrect={isCorrect} explicacion={block.explicacion} onCheck={check} onReset={reset} canCheck={canCheck}>
      <div className="flex items-start gap-2 mb-3">
        <HelpCircle className="w-5 h-5 text-[#004E89] mt-0.5 flex-shrink-0" />
        <div className="text-slate-800 leading-8">
          {segments.map((seg, i) => (
            <React.Fragment key={i}>
              <span>{seg}</span>
              {i < huecos.length && (
                <input
                  type="text" value={values[i]} disabled={submitted}
                  onChange={(e) => setValues((vs) => vs.map((v, idx) => (idx === i ? e.target.value : v)))}
                  className={`inline-block mx-1 px-2 py-0.5 w-32 border-b-2 bg-transparent focus:outline-none text-center ${submitted ? (results[i] ? 'border-emerald-500 text-emerald-700' : 'border-red-500 text-red-700') : 'border-[#004E89]'}`}
                  data-testid="hueco-input"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Shell>
  );
};

const RelacionarQuestion = ({ block, onAnswered }) => {
  const pares = (block.pares || []).filter((p) => String(p.izquierda || '').trim() && String(p.derecha || '').trim());
  const derechas = useMemo(() => [...pares].sort(() => Math.random() - 0.5), [block.id]); // eslint-disable-line
  const [sel, setSel] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const isCorrect = submitted && pares.every((p) => sel[p.id] === p.id);
  const check = () => { setSubmitted(true); onAnswered(); };
  const reset = () => { setSel({}); setSubmitted(false); };
  const canCheck = pares.length > 0 && pares.every((p) => sel[p.id]);
  if (pares.length === 0) return null;
  return (
    <Shell submitted={submitted} isCorrect={isCorrect} explicacion={block.explicacion} onCheck={check} onReset={reset} canCheck={canCheck}>
      {block.pregunta && <Prompt text={block.pregunta} />}
      <div className="space-y-2">
        {pares.map((p) => {
          const ok = submitted && sel[p.id] === p.id;
          return (
            <div key={p.id} className="flex items-center gap-3">
              <span className="flex-1 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">{p.izquierda}</span>
              <span className="text-slate-400">→</span>
              <div className="flex-1">
                <Select value={sel[p.id] || ''} onValueChange={(v) => setSel((s) => ({ ...s, [p.id]: v }))} disabled={submitted}>
                  <SelectTrigger className={submitted ? (ok ? 'border-emerald-400' : 'border-red-400') : ''} data-testid="relacionar-select"><SelectValue placeholder="Elige..." /></SelectTrigger>
                  <SelectContent>
                    {derechas.map((d) => <SelectItem key={d.id} value={d.id}>{d.derecha}</SelectItem>)}
                  </SelectContent>
                </Select>
                {submitted && !ok && <p className="text-xs text-emerald-600 mt-1">Correcto: {p.derecha}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
};

const QuestionBlock = ({ block, onAnswered }) => {
  const formato = block.formato || 'opcion';
  if (formato === 'huecos') return <HuecosQuestion block={block} onAnswered={onAnswered} />;
  if (formato === 'relacionar') return <RelacionarQuestion block={block} onAnswered={onAnswered} />;
  return <OpcionQuestion block={block} onAnswered={onAnswered} />;
};

const LessonBlockRenderer = ({ blocks = [], onProgress }) => {
  const questionIds = blocks.filter((b) => b.tipo === 'pregunta').map((b) => b.id);
  const [answered, setAnswered] = useState([]);

  useEffect(() => {
    if (onProgress) onProgress({ answered: answered.length, total: questionIds.length });
  }, [answered.length, questionIds.length]);

  const markAnswered = (id) => setAnswered((a) => (a.includes(id) ? a : [...a, id]));

  return (
    <div className="space-y-6" data-testid="lesson-block-renderer">
      {proseStyles}
      {blocks.map((block) => {
        if (block.tipo === 'texto') {
          return <div key={block.id} className="lesson-prose max-w-none" dangerouslySetInnerHTML={{ __html: block.contenido || '' }} />;
        }
        if (block.tipo === 'imagen' && block.imagen_url) {
          return (
            <figure key={block.id} className="text-center">
              <img src={resolveImg(block.imagen_url)} alt={block.caption || ''} className="max-w-full rounded-lg border border-slate-200 mx-auto" />
              {block.caption && <figcaption className="text-sm text-slate-500 mt-2 italic">{block.caption}</figcaption>}
            </figure>
          );
        }
        if (block.tipo === 'video' && block.video_url) {
          return (
            <figure key={block.id}>
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {isLocalVideo(block.video_url) ? (
                  <video src={resolveImg(block.video_url)} controls className="w-full h-full" />
                ) : (
                  <iframe src={getEmbedUrl(block.video_url)} className="w-full h-full" allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" title={block.caption || 'video'} />
                )}
              </div>
              {block.caption && <figcaption className="text-sm text-slate-500 mt-2 italic text-center">{block.caption}</figcaption>}
            </figure>
          );
        }
        if (block.tipo === 'destacado') {
          const v = VARIANTS[block.variante] || VARIANTS.nota;
          const VIcon = v.icon;
          return (
            <div key={block.id} className="rounded-lg border-l-4 p-4" style={{ background: v.bg, borderColor: v.color }} data-testid="highlight-block">
              <div className="flex items-center gap-2 mb-1.5 font-semibold" style={{ color: v.color }}>
                <VIcon className="w-5 h-5" /> {block.titulo || v.label}
              </div>
              <div className="lesson-prose max-w-none" dangerouslySetInnerHTML={{ __html: block.contenido || '' }} />
            </div>
          );
        }
        if (block.tipo === 'pregunta') {
          return <QuestionBlock key={block.id} block={block} onAnswered={() => markAnswered(block.id)} />;
        }
        return null;
      })}
    </div>
  );
};

export default LessonBlockRenderer;
